'use client';

import { c as _c } from "react/compiler-runtime";
import { Box, type BoxProps } from '@mui/material';
import { type PropsWithChildren, useId, useState } from 'react';
import { useTooltipPosition } from './useTooltipPosition';
import { getTailStyles, HORIZONTAL_OFFSET, OPPOSITE_DIRECTION_MAP, VERTICAL_OFFSET } from './utils';
type Props = {
  tooltipContent: React.ReactNode;
  tooltipProps?: BoxProps & {
    'data-testid'?: string;
  };
};
export const TooltipCSS = t0 => {
  const $ = _c(32);
  const {
    tooltipContent,
    children,
    tooltipProps
  } = t0;
  let t1;
  if ($[0] !== tooltipProps) {
    t1 = tooltipProps || {};
    $[0] = tooltipProps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let props;
  let tooltipStyles;
  if ($[2] !== t1) {
    ({
      sx: tooltipStyles,
      ...props
    } = t1);
    $[2] = t1;
    $[3] = props;
    $[4] = tooltipStyles;
  } else {
    props = $[3];
    tooltipStyles = $[4];
  }
  const [isVisible, setIsVisible] = useState(false);
  const tooltipId = useId();
  const {
    triggerRef,
    tooltipRef,
    position
  } = useTooltipPosition();
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["left", "right"];
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isLeftOrRight = t2.includes(position);
  const oppositeDirection = OPPOSITE_DIRECTION_MAP[position];
  let t3;
  if ($[6] !== position) {
    t3 = getTailStyles(position);
    $[6] = position;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const tailStyles = t3;
  let t4;
  let t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      position: "relative",
      display: "inline-block",
      "& > div:last-child": {
        opacity: 0,
        visibility: "hidden",
        transition: "opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, transform 0.2s ease-in-out",
        pointerEvents: "none"
      },
      "&:hover > div:last-child": {
        opacity: 1,
        visibility: "visible",
        pointerEvents: "auto"
      },
      "--tooltip-tail-size": "11px"
    };
    t5 = () => setIsVisible(true);
    t6 = () => setIsVisible(false);
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  const t7 = `tooltip-${tooltipId}`;
  let t8;
  if ($[11] !== children || $[12] !== t7 || $[13] !== triggerRef) {
    t8 = <Box ref={triggerRef} aria-describedby={t7}>{children}</Box>;
    $[11] = children;
    $[12] = t7;
    $[13] = triggerRef;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = !isVisible;
  const t10 = `tooltip-${tooltipId}`;
  let t11;
  if ($[15] !== isLeftOrRight || $[16] !== oppositeDirection) {
    t11 = isLeftOrRight ? {
      bottom: "50%",
      transform: "translateY(50%)",
      [oppositeDirection]: `calc(100% + ${HORIZONTAL_OFFSET}px)`
    } : {
      right: "50%",
      transform: "translateX(50%)",
      [oppositeDirection]: `calc(100% + ${VERTICAL_OFFSET}px)`
    };
    $[15] = isLeftOrRight;
    $[16] = oppositeDirection;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== tailStyles || $[20] !== tooltipStyles) {
    t12 = {
      position: "absolute",
      backgroundColor: "white",
      borderRadius: 2,
      boxShadow: "rgba(100, 102, 106, 0.15) 0px 4px 8px 0px, rgba(100, 102, 106, 0.33) 0px 8px 16px 0px",
      border: "1px solid",
      borderColor: "grey.200",
      padding: 3,
      zIndex: 1000,
      minWidth: 200,
      maxWidth: "min(90vw, 90dvw)",
      ...t11,
      ...tailStyles,
      ...tooltipStyles
    };
    $[18] = t11;
    $[19] = tailStyles;
    $[20] = tooltipStyles;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== props || $[23] !== t10 || $[24] !== t12 || $[25] !== t9 || $[26] !== tooltipContent || $[27] !== tooltipRef) {
    t13 = <Box ref={tooltipRef} aria-hidden={t9} role="tooltip" id={t10} sx={t12} {...props}>{tooltipContent}</Box>;
    $[22] = props;
    $[23] = t10;
    $[24] = t12;
    $[25] = t9;
    $[26] = tooltipContent;
    $[27] = tooltipRef;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t8) {
    t14 = <Box sx={t4} onMouseEnter={t5} onMouseLeave={t6}>{t8}{t13}</Box>;
    $[29] = t13;
    $[30] = t8;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
};
export type { TooltipPosition } from './types';